import { Dispatch, SetStateAction } from "react";
import { useUpdateBuzzerAlertStatusMutation } from "../api/network/notificationApiServices";
import { APP_CONFIG } from "../constants/constants";
import { useDispatch, useSelector } from "react-redux";
import { setModalsData, TModalsState, TViewedBuzzerAlert } from "../api/store/commonSlice";
import { BuzzerAlertStatuses, BuzzerAlertUpdatePayload, BuzzerAlertWithUUID } from "../api/types/Notifications";
import { useNavigate } from "react-router-dom";
import { routeUrls } from "../navigation/routeUrls";

const useBuzzerAlertActions = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const modalsState = useSelector((state: { commonReducer: { modals: TModalsState } }) => state.commonReducer.modals);

  const [updateBuzzerAlertAPITrigger, { isLoading }] = useUpdateBuzzerAlertStatusMutation();

  const updateBuzzerAlert = (
    buzzerAlertId: number,
    buzzerAlertUUID: string,
    status: BuzzerAlertUpdatePayload['status'],
    setLoading: Dispatch<SetStateAction<boolean>>,
    successCallback?: () => void,
  ) => {
    setLoading(true);
    updateBuzzerAlertAPITrigger({
      buzzerAlertId,
      status,
    } as BuzzerAlertUpdatePayload)
      .unwrap()
      .then(() => {
        if (APP_CONFIG.DEBUG.ALL) console.log('Buzzer alert resolved', buzzerAlertId)
        dispatch(setModalsData({
          ...modalsState,
          // remove non-pending buzzer alert from viewed list, and add to in-progress list
          ...(status === BuzzerAlertStatuses.INPROGRESS && {
            buzzerAlertsViewed: [
              ...modalsState.buzzerAlertsViewed
                .filter((alert) => alert.uuid !== buzzerAlertUUID),
            ],
            buzzerAlertsInProgress: [
              ...modalsState.buzzerAlertsInProgress,
              ...modalsState.buzzerAlertsViewed
                .filter((alert) => alert.uuid === buzzerAlertUUID),
            ],
          }),
          // remove resolved buzzer alert from in-progress list
          ...(status === BuzzerAlertStatuses.RESOLVED && {
            buzzerAlertsViewed: [
              ...modalsState.buzzerAlertsViewed
                .filter((alert) => alert.uuid !== buzzerAlertUUID),
            ],
            buzzerAlertsInProgress: [
              ...modalsState.buzzerAlertsInProgress
                .filter((alert) => alert.uuid !== buzzerAlertUUID),
            ],
          }),
        }));
        successCallback && successCallback();
      })
      .finally(() => {
        setLoading(false);
      })
  }

  /**
   * DEPRECATED AS buzzerAlertsViewed from store is inaccessible here
   * ---------------------------------------------------------------------
   * Returns viewed buzzer alerts that have not been ignored for n minutes
   * @returns TViewedBuzzerAlert[]
   */
  const getUnignoredBuzzerAlerts = (buzzerAlertsViewed: TViewedBuzzerAlert[]) => {
    console.log('[getUnignoredBuzzerAlerts] buzzerAlertsViewed from store:', buzzerAlertsViewed.length)
    const unignoredBuzzerAlerts =
      buzzerAlertsViewed
        .filter((alert) => {
          const currentTime = new Date().getTime();
          const viewedAt = alert.viewedAt;
          const timeDiff = currentTime - viewedAt;
          const minutes = Math.floor(timeDiff / 1000 / 60);
          if (APP_CONFIG.DEBUG.BUZZER) console.log('Comparing calculated min:', minutes, 'with config min:', APP_CONFIG.BUZZER_ALERTS.IGNORED_PENDING_MINUTES)
          return minutes < APP_CONFIG.BUZZER_ALERTS.IGNORED_PENDING_MINUTES;
        });
    console.log('unignoredBuzzerAlerts', unignoredBuzzerAlerts)
    return unignoredBuzzerAlerts;
  }

  const saveViewedBuzzerAlertsOnViewButtonClick = (buzzerAlertUUIDs: string[]) => {
    // save viewed buzzer alerts
    const viewedAt = new Date().getTime() - 1000; // minus 1 second to ensure next comparison is true
    if (APP_CONFIG.DEBUG.ALL) console.error(`VIEWED AT`, new Date(viewedAt));
    dispatch(setModalsData({
      ...modalsState,
      buzzerAlertsViewed: [
        ...modalsState.buzzerAlertsViewed,
        ...buzzerAlertUUIDs
          .filter((uuid) => !modalsState.buzzerAlertsViewed.map(alert => alert.uuid).includes(uuid))
          .map((uuid) => ({ uuid, viewedAt }))
      ],
      showBuzzerAlertList: false,
      showBuzzerAlertSingle: false,
    }));
  }

  const saveInProgressBuzzerAlertsOnViewButtonClick = (buzzerAlertUUIDs: string[]) => {
    // save in progress buzzer alerts
    const viewedAt = new Date().getTime() - 1000; // minus 1 second to ensure next comparison is true
    if (APP_CONFIG.DEBUG.ALL) console.error(`MARKED IN-PROGRESS AT`, new Date(viewedAt));
    dispatch(setModalsData({
      ...modalsState,
      buzzerAlertsInProgress: [
        ...modalsState.buzzerAlertsInProgress,
        ...buzzerAlertUUIDs
          .filter((uuid) => !modalsState.buzzerAlertsInProgress.map(alert => alert.uuid).includes(uuid))
          .map((uuid) => ({ uuid, viewedAt }))
      ],
      showBuzzerAlertList: false,
      showBuzzerAlertSingle: false,
    }));
  }

  const markViewedInProgressAndNavigateToListing = (unreadBuzzerAlerts: BuzzerAlertWithUUID[]) => {
    const pendingBuzzerAlerts = unreadBuzzerAlerts.filter(alert => alert.status === BuzzerAlertStatuses.PENDING);
    const inProgressBuzzerAlerts = unreadBuzzerAlerts.filter(alert => alert.status === BuzzerAlertStatuses.INPROGRESS);
    const viewedAt = new Date().getTime() - 1000; // minus 1 second to ensure next comparison is true
    // instead of calling saveViewedBuzzerAlertsOnViewButtonClick and saveInProgressBuzzerAlertsOnViewButtonClick
    // we can directly update the store here in one go as the former was only updating the last instruction
    dispatch(setModalsData({
      ...modalsState,
      buzzerAlertsViewed: [
        ...modalsState.buzzerAlertsViewed,
        ...pendingBuzzerAlerts
          .filter((alert) => !modalsState.buzzerAlertsViewed.map(alert => alert.uuid).includes(alert.uuid))
          .map((alert) => ({ uuid: alert.uuid, viewedAt }))
      ],
      buzzerAlertsInProgress: [
        ...modalsState.buzzerAlertsInProgress,
        ...inProgressBuzzerAlerts
          .filter((alert) => !modalsState.buzzerAlertsInProgress.map(alert => alert.uuid).includes(alert.uuid))
          .map((alert) => ({ uuid: alert.uuid, viewedAt }))
      ],
      showBuzzerAlertList: false,
      showBuzzerAlertSingle: false,
    }));
    if (APP_CONFIG.DEBUG.BUZZER) console.error('MARKED VIEWED/IN-PROGRESS AT', new Date());
    navigate(routeUrls.dashboardChildren.panic_alert);
  }

  return {
    updateBuzzerAlert,
    getUnignoredBuzzerAlerts,
    saveViewedBuzzerAlertsOnViewButtonClick,
    saveInProgressBuzzerAlertsOnViewButtonClick,
    markViewedInProgressAndNavigateToListing,
  };
}
export default useBuzzerAlertActions;